import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";

import { ProjectVideos } from "@/components/project-videos";
import { GuestLockProvider } from "@/components/guest-lock";
import { SiteFooter } from "@/components/site-footer";
import { HomeNav } from "@/components/home-nav";
import { CamaraObscuraProject } from "@/components/projects/camara-obscura";
import { PsilocybinProject } from "@/components/projects/psilocybin";
import { ButohProject } from "@/components/projects/butoh";
import { AppropriationProject } from "@/components/projects/appropriation";
import { EditorialProject } from "@/components/projects/editorial";
import { NeurotransitionProject } from "@/components/projects/neurotransition";
import { TopographiesProject } from "@/components/projects/topographies";
import { NecropoliticsProject } from "@/components/projects/necropolitics";
import { AboutMadnessProject } from "@/components/projects/about-madness";
import { ColonizedSexualityProject } from "@/components/projects/colonized-sexuality";

import { getProject, projects } from "@/data/projects";
import { workAt } from "@/data/works-order";
import blackBoxHero from "@/assets/black-box-hero.webp.asset.json";

const SITE = "https://www.luizajesusdoprado.com";
const abs = (src: string) => (src.startsWith("http") ? src : `${SITE}${src}`);

/** Pages whose social thumbnail (og:image) uses the page's own hero image. */
const heroThumb: Record<string, string> = {
  "camara-obscura": abs(blackBoxHero.url),
};


export const Route = createFileRoute("/work/$slug")({
  loader: ({ params }) => {
    if (params.slug === "social-hacker") {
      throw redirect({
        to: "/work/$slug",
        params: { slug: "what-is-madness" },
        statusCode: 301,
      });
    }
    if (params.slug === "o-que-e-a-loucura") {
      throw redirect({
        to: "/work/$slug",
        params: { slug: "what-is-madness" },
        statusCode: 301,
      });
    }
    if (params.slug === "psilocybin-visual-and-sensory-therapy") {
      throw redirect({
        to: "/work/$slug",
        params: { slug: "ecologies-of-perception" },
        statusCode: 301,
      });
    }
    const project = getProject(params.slug);
    if (!project) throw notFound();
    return project;
  },
  head: ({ params, loaderData }) => {
    const url = `https://www.luizajesusdoprado.com/work/${params.slug}`;
    const name =
      loaderData?.slug === "ecologies-of-perception"
        ? "Ecologies of Perception"
        : loaderData?.title;
    const title = name ? `${name} — Luiza Jesus do Prado` : "Work";
    const description = loaderData?.lead.slice(0, 155) ?? "Project by Luiza Jesus do Prado.";
    const thumb = heroThumb[params.slug] ?? (loaderData?.cover ? abs(loaderData.cover) : undefined);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(thumb
          ? [
              { property: "og:image", content: thumb },
              { name: "twitter:image", content: thumb },
            ]
          : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "VisualArtwork",
                name: name ?? title,
                url,
                description,
                dateCreated: loaderData?.year,
                image: [
                  ...(loaderData?.cover ? [abs(loaderData.cover)] : []),
                  ...(loaderData?.images ?? []).slice(0, 20).map((im) => ({
                    "@type": "ImageObject",
                    contentUrl: abs(im.src),
                    caption: im.alt,
                    creator: { "@id": "https://www.luizajesusdoprado.com/#person" },
                    creditText: "Luiza Jesus do Prado",
                    copyrightNotice: "© Luiza Jesus do Prado",
                    license: "https://www.luizajesusdoprado.com/contact",
                    acquireLicensePage: "https://www.luizajesusdoprado.com/contact",
                  })),
                ],
                video: (loaderData?.videos ?? []).map((v) => ({
                  "@type": "VideoObject",
                  name: `${name ?? title} — video`,
                  description,
                  thumbnailUrl:
                    v.poster != null
                      ? abs(v.poster)
                      : v.provider === "youtube"
                        ? `https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`
                        : loaderData?.cover
                          ? abs(loaderData.cover)
                          : undefined,
                  embedUrl:
                    v.provider === "youtube"
                      ? `https://www.youtube.com/embed/${v.id}`
                      : v.provider === "file"
                        ? abs(v.id)
                        : `https://player.vimeo.com/video/${v.id}`,
                  uploadDate: `${(loaderData?.year ?? "2020").slice(0, 4)}-01-01`,
                })),
                creator: {
                  "@type": "Person",
                  "@id": "https://www.luizajesusdoprado.com/#person",
                  name: "Luiza Jesus do Prado",
                  url: "https://www.luizajesusdoprado.com/",
                },
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://www.luizajesusdoprado.com/" },
                  { "@type": "ListItem", position: 2, name: "Works", item: "https://www.luizajesusdoprado.com/works" },
                  { "@type": "ListItem", position: 3, name: name ?? title, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: LockedWorkPage,
});

function LockedWorkPage() {
  return (
    <GuestLockProvider>
      <WorkPage />
    </GuestLockProvider>
  );
}

function WorkPage() {
  const project = Route.useLoaderData();
  const index = projects.findIndex((p) => p.slug === project.slug);
  const next = workAt(project.slug, 1) ?? projects[(index + 1) % projects.length]!;
  const prev = workAt(project.slug, -1);
  const num = String(index + 1).padStart(2, "0");

  const Custom =
    project.slug === "camara-obscura"
      ? CamaraObscuraProject
      : project.slug === "ecologies-of-perception"
        ? PsilocybinProject
        : project.slug === "butoh-caipira"
          ? ButohProject
          : project.slug === "appropriation"
            ? AppropriationProject
            : project.slug === "neurotransition"
              ? NeurotransitionProject
              : project.slug === "topographies-of-violence"
                ? TopographiesProject
                : project.slug === "discourses-of-necropolitics"
                  ? NecropoliticsProject
                  : project.slug === "colonized-sexuality"
                    ? ColonizedSexualityProject
                    : project.slug === "what-is-madness"
                      ? AboutMadnessProject
                      : EditorialProject;

  if (Custom) {
    return (
      <div className="min-h-screen bg-ink text-bone">
        <HomeNav />
        <Custom project={project} next={next} />
        <SiteFooter />
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-ink text-bone">
      <HomeNav />

      {/* ————— Hero: split plate / title ————— */}
      <header className="relative min-h-[92svh] w-full overflow-hidden">
        {project.cover ? (
          <div className="absolute inset-0 md:right-1/2">
            <img
              src={project.cover}
              alt={project.title}
              className="h-full w-full object-cover opacity-70"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/30 to-ink" />
          </div>
        ) : null}

        <div className="relative z-10 mx-auto grid min-h-[92svh] max-w-[1600px] grid-cols-1 items-center gap-12 px-6 pb-24 pt-40 md:grid-cols-12 md:px-12">
          <div className="md:col-span-6 md:col-start-7">
            <div className="flex items-center gap-5 font-mono text-[9px] uppercase tracking-[0.4em] text-bone/65">
              <span>{num}</span>
              <span className="h-px w-12 bg-bone/25" />
              <span>{project.year || "—"}</span>
            </div>

            <h1 className="mt-8 font-display text-[clamp(2.6rem,7vw,6rem)] font-light leading-[0.92] tracking-[-0.02em] text-bone">
              {project.title}
            </h1>

            {project.lead ? (
              <p className="mt-10 max-w-md border-t border-bone/20 pt-8 text-[clamp(1rem,1.5vw,1.35rem)] font-light italic leading-[1.55] text-bone/75">
                {project.lead}
              </p>
            ) : null}

            {project.body[0] ? (
              <p className="mt-8 max-w-sm font-mono text-[10px] uppercase leading-[2.1] tracking-[0.14em] text-bone/65">
                {project.body[0].slice(0, 300)}
                {project.body[0].length > 300 ? "…" : ""}
              </p>
            ) : null}
          </div>
        </div>

        <div className="pointer-events-none absolute inset-x-6 bottom-8 z-10 flex justify-between font-mono text-[9px] uppercase tracking-[0.34em] text-bone/65 md:inset-x-12">
          <span>Luiza Jesus do Prado</span>
          <span>
            {""}
            {project.videos.length ? ` · ${project.videos.length} film` : ""}
          </span>
        </div>
      </header>

      <main id="main-content">
        {/* Text */}
        {project.body.length ? (
          <section className="border-t border-bone/12">
            <div className="mx-auto grid max-w-[1600px] gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32">
              <h2 className="font-mono text-[9px] uppercase tracking-[0.36em] text-bone/65 md:col-span-3">
                Text
              </h2>
              <div className="space-y-8 md:col-span-7">
                {project.body.map((paragraph) => (
                  <p
                    key={paragraph.slice(0, 40)}
                    className="text-[15px] font-light leading-[1.9] text-bone/70"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <ProjectVideos videos={project.videos} title={project.title} />

        {/* Plates */}
        {project.images.length ? (
          <section className="mx-auto max-w-[1600px] px-6 pb-28 md:px-12">
            <div className="grid gap-x-12 gap-y-20 md:grid-cols-12">
              {project.images.map((image, i) => {
                const pattern = i % 4;
                const col =
                  pattern === 0
                    ? "md:col-span-12"
                    : pattern === 1
                      ? "md:col-span-7"
                      : pattern === 2
                        ? "md:col-span-5 md:pt-28"
                        : "md:col-span-8 md:col-start-4";
                return (
                  <figure key={`${image.src}-${i}`} className={col}>
                    <img
                      src={image.src}
                      alt={image.alt}
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-[900ms] hover:scale-[1.01]"
                    />
                    <figcaption className="mt-4 flex gap-5 font-mono text-[9px] uppercase tracking-[0.28em] text-bone/65">
                      <span>PL. {String(i + 1).padStart(2, "0")}</span>
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </section>
        ) : null}

        {prev ? (
          <Link
            to="/work/$slug"
            params={{ slug: prev.slug }}
            className="group flex flex-wrap items-baseline justify-between gap-4 border-t border-bone/12 px-6 py-9 md:px-12"
          >
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-bone/65">
              ← Previous project
            </span>
            <span className="font-display text-[clamp(1.1rem,2.4vw,1.9rem)] font-light uppercase leading-none text-bone/70 transition-all duration-500 group-hover:-translate-x-3 group-hover:text-bone">
              {prev.title}
            </span>
          </Link>
        ) : null}

        <Link
          to="/work/$slug"
          params={{ slug: next.slug }}
          className="group flex flex-wrap items-baseline justify-between gap-4 border-t border-bone/12 px-6 py-14 md:px-12 md:py-20"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.36em] text-bone/65">
            Next — {String(((index + 1) % projects.length) + 1).padStart(2, "0")}
          </span>
          <span className="font-display text-[clamp(1.1rem,2.4vw,1.9rem)] font-light leading-none text-bone/80 transition-all duration-500 group-hover:translate-x-3 group-hover:text-bone">
            {next.title}
          </span>

        </Link>
      </main>

      <SiteFooter />
    </div>
  );
}
